"use client";

import type React from "react";
import { useCallback, useRef, useState } from "react";
import { NoResults } from "@/components/common/no-results";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { useMenuResponseTimeEvent } from "@/features/analytics/hooks/use-menu-response-time-event";
import { DietMenuModalContent } from "@/features/diets/components/diet-menu-modal/diet-menu-modal-content";
import { getCalorific } from "@/features/diets/helpers/get-calorific";
import type { DietPricingComplex } from "@/features/diets/types/diet-pricing-complex.type";
import { cn } from "@/lib/utils";
import { useDiet } from "../../hooks/use-diet";

const TARGET_CALORIFIC_DEFAULT = 1500;
const MAX_ALLOWED_MEALS = 6;

type DietMenuModalProps = {
  trigger: React.ReactNode;
  dietId: string;
  showCta?: boolean;
  configuration?: Record<string, string | number | string[]>;
  variant?: string;
  kcal?: string;
  type?: "multidiet" | "default";
  numberOfDays?: number;
  price?: number;
  priceComplex?: DietPricingComplex;
  trackMenuResponseTime?: boolean;
};

export const DietMenuModal = ({
  trigger,
  dietId,
  showCta,
  configuration,
  variant,
  kcal,
  type,
  numberOfDays,
  price,
  priceComplex,
  trackMenuResponseTime = false,
}: DietMenuModalProps) => {
  const [open, setOpen] = useState(false);
  const menuOpenStartTimeRef = useRef<number | null>(null);
  const hasTrackedMenuResponseTimeRef = useRef(false);
  const numberOfMealsParamRaw = configuration?.numberOfMeals as string;
  const { sendMenuResponseTimeEvent } = useMenuResponseTimeEvent();
  const { data } = useDiet({ dietId, options: { enabled: open } });

  const selection = getCalorific({
    data,
    variant,
    numberOfMeals: numberOfMealsParamRaw,
    kcal,
    targetDefaultCalories: TARGET_CALORIFIC_DEFAULT,
    maxAllowedMeals: MAX_ALLOWED_MEALS,
  });

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      setOpen(isOpen);

      if (!trackMenuResponseTime) {
        return;
      }

      if (isOpen) {
        menuOpenStartTimeRef.current = performance.now();
        hasTrackedMenuResponseTimeRef.current = false;
        return;
      }

      menuOpenStartTimeRef.current = null;
      hasTrackedMenuResponseTimeRef.current = false;
    },
    [trackMenuResponseTime],
  );

  const handleInitialMenuLoaded = useCallback(() => {
    if (!trackMenuResponseTime) {
      return;
    }

    if (hasTrackedMenuResponseTimeRef.current) {
      return;
    }

    const menuOpenStartTime = menuOpenStartTimeRef.current;

    if (menuOpenStartTime === null) {
      return;
    }

    hasTrackedMenuResponseTimeRef.current = true;

    sendMenuResponseTimeEvent({
      loadingTimeSec: Math.round(
        (performance.now() - menuOpenStartTime) / 1000,
      ),
    });
  }, [sendMenuResponseTimeEvent, trackMenuResponseTime]);

  return (
    <Dialog modal={true} open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        className={cn(
          "h-dvh max-h-dvh max-w-screen gap-0 rounded-none p-0 md:h-[calc(100dvh-100px)] md:overflow-hidden md:rounded-xl",
          type === "multidiet" ? "lg:min-w-[900px]" : "lg:min-w-[1080px]",
        )}
        closeButton={false}
        data-pw="check-menu-modal"
      >
        {data ? (
          <DietMenuModalContent
            availableCalorifics={selection.availableCalorifics}
            configuration={configuration}
            data={data}
            dietId={dietId}
            numberOfDays={numberOfDays}
            price={price}
            priceComplex={priceComplex}
            onInitialMenuLoaded={handleInitialMenuLoaded}
            selectedCalorific={{
              id: selection.id,
              calories: selection.calories,
            }}
            showCta={showCta}
            type={type}
          />
        ) : (
          <NoResults />
        )}
      </DialogContent>
    </Dialog>
  );
};
